import React, { useState, ReactNode, useEffect } from "react"
import { AuthContext, AuthContextType } from "./authContext"

import { httpClient } from "../http"
import { LocalToken } from "../../utils/localStorage"
import { UserRole, AuthSession } from "./auth.types"
import axios from "axios"
import { useStaticContext } from "../static"

const wrongCredentialsMessagePool = [
  "Identifiants incorrects",
  "{wrongCredentialsErrorMessage}",
  "On se connaît ? Non, j'ai dû confondre.",
  "You shall not pass !!",
  "Ca arrive à tout le monde d'oublier des mots de passe...",
  "On bougera pas tant que les identifiants sont pas bons.",
  "Une erreur attendue s'est produite...",
]

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [userId, setUserId] = useState<string | null>(null)
  const [username, setUsername] = useState<string | null>(null)
  const [role, setRole] = useState<UserRole | null>(null)
  const [gdaAccess, setGdaAccess] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const { reload: reloadStatic } = useStaticContext()

  useEffect(() => {
    const restoreSession = async () => {
      LocalToken.reset()
      try {
        const session = await httpClient.instance.get<AuthSession>("auth/session")
        setRole(session.role)
        setGdaAccess(session.gdaAccess)
        setUserId(String(session.user))
        setUsername(session.username)
      } catch (error) {
        if (!axios.isAxiosError(error) || error.response?.status !== 401) {
          console.error("Unable to restore authentication session:", error)
        }
      } finally {
        setLoading(false)
      }
    }
    restoreSession()
  }, [])

  const logout = async () => {
    try {
      await httpClient.instance.post("auth/logout")
    } catch (error) {
      console.error("Unable to clear authentication cookie:", error)
    } finally {
      setUserId(null)
      setRole(null)
      setGdaAccess(false)
      setUsername(null)
    }
  }

  const updateSession = (session: AuthSession) => {
    setUserId(String(session.user))
    setRole(session.role)
    setUsername(session.username)
    setGdaAccess(session.gdaAccess)
  }

  const contextValue: AuthContextType = {
    loading,
    role,
    gdaAccess,

    login: async ({ username, password }) => {
      try {
        setLoading(true)
        if (!(username.length > 0) || !(password.length > 0)) {
          throw new Error("Veuillez renseinger un identifiant et un mot de passe")
        }
        const formData = new FormData()
        formData.append("username", username)
        formData.append("password", password)
        const session: AuthSession = await httpClient.instance.request<AuthSession>({
          url: "token",
          method: "POST",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        })
        updateSession(session)
      } catch (err: any) {
        if (axios.isAxiosError(err)) {
          if (err.response?.status === 401) {
            throw new Error(
              wrongCredentialsMessagePool[
                Math.floor(Math.max((Math.random() * 10 - 9) * wrongCredentialsMessagePool.length, 0))
              ],
            )
          }
        }
        throw new Error(err.message || "Une erreur s'est produite...")
      } finally {
        await reloadStatic()
        setLoading(false)
      }
    },

    VRlogin: async (params: string) => {
      setLoading(true)
      try {
        const session: AuthSession = await httpClient.instance.request<AuthSession>({
          url: `auth/callback${params}`,
          method: "GET",
          withCredentials: true,
        })
        updateSession(session)
      } catch (err) {
        if (axios.isAxiosError(err)) {
          if (err.response?.status === 401) {
            throw new Error(
              wrongCredentialsMessagePool[
                Math.floor(Math.max((Math.random() * 10 - 9) * wrongCredentialsMessagePool.length, 0))
              ],
            )
          }
        }
        throw new Error("Une erreur s'est produite...")
      } finally {
        await reloadStatic()
        setLoading(false)
      }
    },

    logout,
    isAuth: () => userId !== null,
    username: username,
    userId: userId ? parseInt(userId) : null,
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}
